import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import {
  QUICK_MINUTE_VALUES,
  clampHour,
  clampMinute,
  isOptionDisabled,
} from '@/components/ui/timePickerUtils';
import type { SelectOption } from '@/components/ui/Select';

export type TimePickerPanelProps = {
  hour: string;
  minute: string;
  onHourChange: (hour: string) => void;
  onMinuteChange: (minute: string) => void;
  hourOptions?: SelectOption[];
  minuteOptions?: SelectOption[];
  showApply?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  className?: string;
  embedded?: boolean;
};

function TimeSpinnerColumn({
  label,
  value,
  onIncrement,
  onDecrement,
  incrementDisabled,
  decrementDisabled,
}: {
  label: string;
  value: string;
  onIncrement: () => void;
  onDecrement: () => void;
  incrementDisabled?: boolean;
  decrementDisabled?: boolean;
}) {
  return (
    <div className="time-spinner-column">
      <span className="time-spinner-label">{label}</span>
      <button
        type="button"
        className="time-spinner-btn"
        aria-label={`${label} up`}
        disabled={incrementDisabled}
        onClick={onIncrement}
      >
        <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
      </button>
      <span className="time-spinner-value" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="time-spinner-btn"
        aria-label={`${label} down`}
        disabled={decrementDisabled}
        onClick={onDecrement}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
      </button>
    </div>
  );
}

export function TimePickerPanel({
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  hourOptions,
  minuteOptions,
  showApply = true,
  onApply,
  applyDisabled,
  className,
  embedded = false,
}: TimePickerPanelProps) {
  const { t } = useTranslation('common');
  const hourDisabled = (value: string) => isOptionDisabled(hourOptions ?? [], value);
  const minuteDisabled = (value: string) => isOptionDisabled(minuteOptions ?? [], value);

  const adjustHour = (delta: number) => {
    let next = Number(hour) + delta;
    let guard = 0;
    while (guard < 24) {
      const str = String(clampHour(next)).padStart(2, '0');
      if (!hourDisabled(str)) {
        onHourChange(str);
        return;
      }
      next += delta;
      guard += 1;
    }
  };

  const adjustMinute = (delta: number) => {
    let next = Number(minute) + delta;
    let guard = 0;
    while (guard < 60) {
      const str = String(clampMinute(next)).padStart(2, '0');
      if (!minuteDisabled(str)) {
        onMinuteChange(str);
        return;
      }
      next += delta;
      guard += 1;
    }
  };

  const nextHourUp = String(clampHour(Number(hour) + 1)).padStart(2, '0');
  const nextHourDown = String(clampHour(Number(hour) - 1)).padStart(2, '0');
  const nextMinuteUp = String(clampMinute(Number(minute) + 1)).padStart(2, '0');
  const nextMinuteDown = String(clampMinute(Number(minute) - 1)).padStart(2, '0');

  return (
    <div className={cn('time-picker-panel', embedded && 'time-picker-panel--embedded', className)}>
      <div className="time-spinner-row">
        <TimeSpinnerColumn
          label={t('labels.hour')}
          value={hour}
          onIncrement={() => adjustHour(1)}
          onDecrement={() => adjustHour(-1)}
          incrementDisabled={hourDisabled(nextHourUp)}
          decrementDisabled={hourDisabled(nextHourDown)}
        />
        <span className="time-spinner-colon" aria-hidden>
          :
        </span>
        <TimeSpinnerColumn
          label={t('labels.minute')}
          value={minute}
          onIncrement={() => adjustMinute(1)}
          onDecrement={() => adjustMinute(-1)}
          incrementDisabled={minuteDisabled(nextMinuteUp)}
          decrementDisabled={minuteDisabled(nextMinuteDown)}
        />
      </div>

      <div className="time-quick-row">
        {QUICK_MINUTE_VALUES.filter((_, i) => i % 3 === 0).map((minVal) => {
          const preset = `${hour}:${minVal}`;
          const disabled = minuteDisabled(minVal);
          const selected = minute === minVal;
          return (
            <button
              key={preset}
              type="button"
              disabled={disabled}
              onClick={() => onMinuteChange(minVal)}
              className={cn('time-quick-chip', selected && 'time-quick-chip--selected')}
            >
              {hour}:{minVal}
            </button>
          );
        })}
      </div>

      {showApply && onApply && (
        <Button type="button" className="mt-3 w-full" disabled={applyDisabled} onClick={onApply}>
          {t('actions.apply', { defaultValue: 'Apply' })}
        </Button>
      )}
    </div>
  );
}
